import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../design/Card';
import EmptyState from '../design/EmptyState';
import PasukWords from './PasukWords';
import TorahScrollArt from './TorahScrollArt';
import WaveformPlayer from './WaveformPlayer';
import { activeWordIndex, useAlignment } from '../utils/alignment';
import { findPasuk, formatPasukRef } from '../utils/pasuk';
import type { PasukRef } from '../types';

interface PasukCardProps {
  pasuk: PasukRef | null;
  rabbiAudioUrl: string;
  /** Rabbi word timings; words are colored while the rabbi recording plays. */
  alignmentUrl: string;
  /** Controls under the player (record / compare ...). */
  children?: React.ReactNode;
}

/** The current pasuk on parchment, with the rabbi recording and karaoke word coloring. */
const PasukCard: React.FC<PasukCardProps> = ({ pasuk, rabbiAudioUrl, alignmentUrl, children }) => {
  const { t, i18n } = useTranslation();
  const verse = findPasuk(pasuk);
  const timings = useAlignment(verse ? alignmentUrl : '');
  const [activeIndex, setActiveIndex] = useState(-1);

  const onTime = useCallback(
    (seconds: number, playing: boolean): void => {
      setActiveIndex(playing && timings ? activeWordIndex(timings, seconds) : -1);
    },
    [timings],
  );

  if (!pasuk || !verse) {
    return (
      <Card>
        <EmptyState
          title={t('school_no_current_pasuk_title')}
          description={t('school_no_current_pasuk_description')}
        />
      </Card>
    );
  }

  return (
    <section className="relative overflow-hidden rounded-3xl border border-school-parchment-line bg-school-parchment p-5 sm:p-8">
      <TorahScrollArt className="pointer-events-none absolute -bottom-6 end-0 h-48 w-48 text-school-parchment-ink opacity-30 sm:h-64 sm:w-64" />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute start-5 top-2 font-pasuk text-6xl font-bold leading-none text-school-gold sm:start-8 sm:text-7xl"
      >
        ”
      </span>

      <div className="relative">
        <PasukWords
          hebrew={verse.hebrew}
          activeIndex={activeIndex}
          className="pe-2 pt-8 font-pasuk text-[1.75rem] font-bold leading-[1.9] text-school-ink sm:pt-10 sm:text-4xl sm:leading-[1.8] lg:text-[2.75rem]"
        />
        <p className="mb-5 mt-2 text-sm text-school-muted sm:mb-6">
          {formatPasukRef(pasuk, i18n.language)}
        </p>

        <WaveformPlayer src={rabbiAudioUrl} label={t('school_play')} onTime={onTime} />

        {children && <div className="mt-4">{children}</div>}
      </div>
    </section>
  );
};

export default PasukCard;
